import { resolveAppBranding } from "@/config/appBranding";
import { BrandLogo } from "@/components/auth/BrandLogo";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

type DeviceOS = 'ios' | 'android' | 'other';

const detectDeviceOS = (): DeviceOS => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';
  if (/android/.test(userAgent)) return 'android';
  return 'other';
};

const resolveAppBrandingFromKey = (key: string | null): string | null =>
  key ? resolveAppBranding(key).key : null;

export default function OpenAppPage() {
  const { t } = useTranslation("appui");
  const [showFallback, setShowFallback] = useState(false);
  const [deviceOS, setDeviceOS] = useState<DeviceOS>('other');
  const [searchParams] = useSearchParams();

  const appName = searchParams.get('appName') || resolveAppBrandingFromKey(searchParams.get('app'));
  const isRedirected = searchParams.get('redirected') === '1';
  const { deepLinkShortcut, brandName, appleStoreLink, playStoreLink } = useThemeCustomization(appName);

  useEffect(() => {
    setDeviceOS(detectDeviceOS());
  }, []);

  useEffect(() => {
    // Prevent back navigation to web app
    if (isRedirected) {
      window.history.pushState(null, '', window.location.href);
      const handlePopState = () => {
        window.history.pushState(null, '', window.location.href);
      };
      window.addEventListener('popstate', handlePopState);
      
      // Show fallback immediately for blocked users
      setShowFallback(true);
      
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }

    const success = searchParams.get('success');
    const deepLink = `${deepLinkShortcut}://login${success ? `?success=${success}` : ''}`;
    window.location.href = deepLink;

    const timeout = setTimeout(() => {
      setShowFallback(true);
    }, 1500);

    return () => clearTimeout(timeout);
  }, [searchParams, isRedirected, deepLinkShortcut]);

  const handleRetry = () => {
    if (isRedirected) {
      // Don't attempt deep link for blocked users - they must download the app
      return;
    }
    const success = searchParams.get('success');
    const deepLink = `${deepLinkShortcut}://login${success ? `?success=${success}` : ''}`;
    window.location.href = deepLink;
  };

  const getDownloadMessage = () => {
    if (isRedirected) {
      return t("openApp.accessDeniedMessage");
    }
    if (deviceOS === 'ios') {
      return t("openApp.iosNotDetected");
    }
    if (deviceOS === 'android') {
      return t("openApp.androidNotDetected");
    }
    return t("openApp.otherNotDetected");
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 text-center">
        <BrandLogo appName={appName} />

      {!showFallback && !isRedirected && (
        <>
          <h1 className="text-2xl font-bold mb-2 text-gray-800">{t("openApp.redirecting")}</h1>
          <p className="text-gray-600 mb-4">{t("openApp.pleaseWait")}</p>
        </>
      )}

      {showFallback && (
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          {isRedirected && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-semibold">
                {t("openApp.accessDeniedTitle")}
              </p>
              <p className="text-xs text-red-600 mt-1">
                {t("openApp.accessDeniedReason")}
              </p>
            </div>
          )}
          
          <h1 className="text-xl font-bold mb-4 text-gray-800">{t("openApp.openAppTitle", { brandName })}</h1>
          <p className="mb-6 text-sm text-gray-600">
            {getDownloadMessage()}
          </p>

          {!isRedirected && (
            <button
              onClick={handleRetry}
              className="w-full mb-6 py-3 rounded-lg bg-orange-600 text-white font-semibold hover:bg-green-700 transition"
            >
              {t("openApp.openInApp")}
            </button>
          )}

          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            {(deviceOS === 'ios' || deviceOS === 'other') && (
              <a
                href={appleStoreLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 h-20 bg-black rounded-xl overflow-hidden flex items-center justify-center hover:bg-gray-900 transition"
              >
                <img
                  src="/lovable-uploads/app_store_badge.svg"
                  alt="Download on the App Store"
                  className="w-full h-full object-contain p-3"
                />
              </a>
            )}

            {(deviceOS === 'android' || deviceOS === 'other') && (
              <a
                href={playStoreLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 h-20 bg-green-600 rounded-xl overflow-hidden flex items-center justify-center hover:bg-green-700 transition"
              >
                <img
                  src="/lovable-uploads/google-play-badge.png"
                  alt="Get it on Google Play"
                  className="play-badge"
                />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
